import React, { useState, useEffect } from "react";
import ImageGallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";
import { useLocation } from 'react-router-dom';
import { Col, Container, Row, Modal } from "react-bootstrap";
import { FaImages, FaVideo, FaTimes } from "react-icons/fa";
import { RiFlagLine } from "react-icons/ri";
import '../css/initiatives.css';
import * as contentService from '../services/contentService';

// Fallback images
import img5 from "../assets/Initiatives/images/L5.jpg";
import img6 from "../assets/Initiatives/images/L6.jpg";
import img7 from "../assets/Initiatives/images/L7.jpg";
import img8 from "../assets/Initiatives/images/L8.jpg";
import img9 from "../assets/Initiatives/images/L9.jpg";
import img10 from "../assets/Initiatives/images/L10.jpg";
import img11 from "../assets/Initiatives/images/L11.jpg";
import img12 from "../assets/Initiatives/images/L12.jpg";
import img13 from "../assets/Initiatives/images/L13.jpg";
import img14 from "../assets/Initiatives/images/L14.jpg";
import img15 from "../assets/Initiatives/images/L15.jpg";
import img16 from "../assets/Initiatives/images/L16.jpg";
import img17 from "../assets/Initiatives/images/L17.jpg";
import img18 from "../assets/Initiatives/images/L18.jpg";
import img19 from "../assets/Initiatives/images/L19.jpg";
import img20 from "../assets/Initiatives/images/L20.jpg";
import img21 from "../assets/Initiatives/images/L21.jpg";
import img22 from "../assets/Initiatives/images/L22.jpg";
import img23 from "../assets/Initiatives/images/L23.jpg";

const DEFAULT_LOCAL_IMAGES = [
    img5, img6, img7, img8, img9, img10,
    img11, img12, img13, img14, img15, img16,
    img17, img18, img19, img20, img21, img22, img23
];

const DEFAULT_VIDEOS = [
    {
        title: "Activity Video #1",
        url: "https://firebasestorage.googleapis.com/v0/b/lovingarms.firebasestorage.app/o/videos%2F1.mp4?alt=media&token=32f5751b-6dda-449c-93e6-b13fb3666f31"
    },
    {
        title: "Activity Video #2",
        url: "https://firebasestorage.googleapis.com/v0/b/lovingarms.firebasestorage.app/o/videos%2F2.mp4?alt=media&token=a45306d4-f891-4e26-8cdd-38ed1f34ded1"
    },
    {
        title: "Activity Video #3",
        url: "https://firebasestorage.googleapis.com/v0/b/lovingarms.firebasestorage.app/o/videos%2F3.mp4?alt=media&token=ed52c148-5128-45dc-a334-6952dc7ee735"
    },
    {
        title: "Activity Video #4",
        url: "https://firebasestorage.googleapis.com/v0/b/lovingarms.firebasestorage.app/o/videos%2F4.mp4?alt=media&token=74fbc4c8-cb34-410e-ba3c-bca28d95953b"
    },
    {
        title: "Activity Video #5",
        url: "https://firebasestorage.googleapis.com/v0/b/lovingarms.firebasestorage.app/o/videos%2F5.mp4?alt=media&token=f38cd5d5-32a4-4bde-86a3-140ebfbb2103"
    }
];

const Initiatives = () => {
    const [selectedImage, setSelectedImage] = useState(null);
    const [filterTab, setFilterTab] = useState('all'); // 'all', 'photos', 'videos'
    const [photos, setPhotos] = useState(DEFAULT_LOCAL_IMAGES);
    const [videos, setVideos] = useState(DEFAULT_VIDEOS);
    const location = useLocation();
    const isHome = location.pathname === '/';

    useEffect(() => {
        const fetchGalleryMedia = async () => {
            try {
                const data = await contentService.getGallery();
                if (data && data.length > 0) {
                    const dynamicPhotos = data.filter(item => item.type === 'image' && item.url).map(i => i.url);
                    const dynamicVideos = data.filter(item => item.type === 'video' && item.url);

                    if (dynamicPhotos.length > 0) {
                        setPhotos(dynamicPhotos);
                    }
                    if (dynamicVideos.length > 0) {
                        setVideos(dynamicVideos);
                    }
                }
            } catch (err) {
                console.error("Failed to load live gallery items:", err);
            }
        };

        fetchGalleryMedia();
    }, []);

    const carouselItems = photos.map(url => ({
        original: url,
        thumbnail: url
    }));

    return (
        <section className="modern-initiatives-section py-5">
            {isHome ? (
                <Container>
                    <div className="text-center mb-4">
                        <span className="badge-tag">Our Living Journey</span>
                        <h2 className="section-title mt-2">
                            <RiFlagLine className="me-2 text-warning" /> Moments That Capture Hope & Transformation
                        </h2>
                        <p className="text-muted max-w-600 mx-auto">
                            Snapshots from daily life, studies, festivals, and activities at Loving Arms Orphanage.
                        </p>
                    </div>

                    <div className="home-gallery-wrapper">
                        <ImageGallery
                            items={carouselItems}
                            showThumbnails={false}
                            showPlayButton={false}
                            showFullscreenButton={false}
                            useBrowserFullscreen={true}
                            autoPlay={true}
                            slideDuration={600}
                            slideInterval={4000}
                        />
                    </div>
                </Container>
            ) : (
                <Container className="gallery-page-container">
                    <div className="text-center mb-5">
                        <span className="badge-tag">Media Gallery</span>
                        <h2 className="section-title mt-2">
                            <FaImages className="me-2 text-warning" /> Life at Loving Arms
                        </h2>
                        <p className="text-muted max-w-600 mx-auto">
                            Explore authentic photos and video moments celebrating the resilient spirit of our children.
                        </p>

                        {/* Filter Tabs */}
                        <div className="filter-pill-container mt-4">
                            <button
                                className={`filter-pill ${filterTab === 'all' ? 'active' : ''}`}
                                onClick={() => setFilterTab('all')}
                            >
                                All Media
                            </button>
                            <button
                                className={`filter-pill ${filterTab === 'photos' ? 'active' : ''}`}
                                onClick={() => setFilterTab('photos')}
                            >
                                <FaImages className="me-1" /> Photos ({photos.length})
                            </button>
                            <button
                                className={`filter-pill ${filterTab === 'videos' ? 'active' : ''}`}
                                onClick={() => setFilterTab('videos')}
                            >
                                <FaVideo className="me-1" /> Videos ({videos.length})
                            </button>
                        </div>
                    </div>

                    {/* Photo Grid */}
                    {(filterTab === 'all' || filterTab === 'photos') && (
                        <div className="mb-5">
                            <h4 className="gallery-group-title mb-4">
                                <FaImages className="text-warning me-2" /> Photographs
                            </h4>
                            <Row className="g-4">
                                {photos.map((img, index) => (
                                    <Col key={index} xs={12} sm={6} md={4} lg={3}>
                                        <div
                                            className="modern-media-card"
                                            onClick={() => setSelectedImage(img)}
                                        >
                                            <img
                                                src={img}
                                                alt={`Activity moment ${index + 1}`}
                                                loading="lazy"
                                                className="media-card-img"
                                            />
                                            <div className="media-card-overlay">
                                                <span>View Full Photo</span>
                                            </div>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </div>
                    )}

                    {/* Video Grid */}
                    {(filterTab === 'all' || filterTab === 'videos') && (
                        <div className="mb-4">
                            <h4 className="gallery-group-title mb-4">
                                <FaVideo className="text-warning me-2" /> Video Highlights
                            </h4>
                            <Row className="g-4">
                                {videos.map((vidItem, index) => (
                                    <Col key={index} xs={12} sm={6} md={4} lg={4}>
                                        <div className="modern-video-card">
                                            {vidItem.url && (vidItem.url.includes('youtube.com') || vidItem.url.includes('youtu.be')) ? (
                                                <div className="ratio ratio-16x9">
                                                    <iframe
                                                        src={(() => {
                                                            let base = vidItem.url.replace('watch?v=', 'embed/').split('&')[0];
                                                            const separator = base.includes('?') ? '&' : '?';
                                                            return `${base}${separator}autoplay=1&mute=1&playsinline=1&loop=1`;
                                                        })()}
                                                        title={vidItem.title || `Activity Video #${index + 1}`}
                                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                        allowFullScreen
                                                    ></iframe>
                                                </div>
                                            ) : (
                                                <video
                                                    src={vidItem.url}
                                                    preload="auto"
                                                    controls
                                                    autoPlay
                                                    muted
                                                    defaultMuted
                                                    playsInline
                                                    loop
                                                    onLoadedMetadata={(e) => {
                                                        e.target.muted = true;
                                                        const p = e.target.play();
                                                        if (p !== undefined) {
                                                            p.catch(() => {});
                                                        }
                                                    }}
                                                    className="media-card-video"
                                                />
                                            )}
                                            <div className="video-card-meta">
                                                <h6>{vidItem.title || `Activity Video #${index + 1}`}</h6>
                                            </div>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </div>
                    )}

                    {/* Full Size Image Modal */}
                    <Modal
                        show={selectedImage !== null}
                        onHide={() => setSelectedImage(null)}
                        size="lg"
                        centered
                        className="modern-gallery-modal"
                    >
                        <Modal.Body className="p-0 position-relative text-center bg-black rounded-3 overflow-hidden">
                            <button
                                className="modal-close-btn"
                                onClick={() => setSelectedImage(null)}
                            >
                                <FaTimes />
                            </button>
                            <img
                                src={selectedImage}
                                alt="Expanded View"
                                className="img-fluid modal-expanded-img"
                            />
                        </Modal.Body>
                    </Modal>
                </Container>
            )}
        </section>
    );
};

export default Initiatives;
